import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill, formatDate, formatNumber, todayIso } from '@/components/live';
import { usePickers } from '@/hooks/usePickers';
import { optionLabel } from '@/lib/practiceLists';

interface ChamberRow {
  id: string; chamber_code: string | null; site_id: string; site_name: string; village_name: string | null;
  name: string; chamber_type: string; capacity: number | null; capacity_uom: string | null; status: string;
  last_calibration_on: string | null; next_maintenance_on: string | null; commissioned_on: string | null;
}

const CHAMBER_TYPES = [
  { id: 'kiln', label: 'Seasoning kiln' },
  { id: 'chemical_tank', label: 'Chemical treatment tank' },
];

export const CHAMBER_STATUS = [
  { id: 'idle', label: 'Idle' },
  { id: 'new_batch_loaded', label: 'New batch loaded' },
  { id: 'running', label: 'Running' },
  { id: 'emptied', label: 'Emptied' },
  { id: 'under_repair', label: 'Under repair' },
  { id: 'repaired', label: 'Repaired' },
];

/**
 * The twelve moves `ops.fn_chamber_status_guard` allows, mirrored here only to tell the operator
 * what comes next. The trigger is the rule; a move it refuses comes back with the same list.
 */
const NEXT: Record<string, string[]> = {
  idle: ['new_batch_loaded'],
  new_batch_loaded: ['running', 'under_repair'],
  running: ['running', 'emptied', 'under_repair'],
  emptied: ['new_batch_loaded', 'under_repair'],
  under_repair: ['repaired'],
  repaired: ['new_batch_loaded', 'running', 'emptied'],
};

const nextSteps = (status: string) => (NEXT[status] ?? []).map((id) => optionLabel(CHAMBER_STATUS, id)).join(', ');

/**
 * Seasoning kilns and chemical treatment tanks (owner, 2026-09-26: "Add seasoning chamber and its
 * management"). One register for both, each a child of the site that houses it, with the live step
 * it is on. The step moves only along the twelve transitions of the reference process - Running to
 * Running is the daily log, and a repair part-way through a cycle resumes it rather than starting
 * again.
 */
export default function ChambersScreen() {
  const pick = usePickers(['sites', 'units']);
  const today = todayIso();
  return (
    <RegisterScreen<ChamberRow>
      secondary={{ sum: (row) => row.capacity, unit: 'capacity', digits: 0 }}
      addLabel="Add chamber" exportName="seasoning-chambers" recordLabel={(row) => `chamber ${row.chamber_code ?? row.name}`}
      recordNoun="chamber"
      screenKey="site.register" searchColumns={['chamber_code', 'name', 'site_name', 'village_name']}
      subtitle="Seasoning kilns and chemical tanks at each processing site - what each is doing now, and when it is due for maintenance."
      table="ops.chambers" title="Seasoning chambers" view="chambers" orderBy={{ column: 'name', ascending: true }}
      columns={[
        { key: 'name', label: 'Chamber', render: (row) => <span className="cell-stack"><strong>{row.name}</strong><small className="muted">{row.chamber_code ?? ''} · {optionLabel(CHAMBER_TYPES, row.chamber_type)}</small></span> },
        { key: 'site_name', label: 'Site', render: (row) => <span className="cell-stack"><span>{row.site_name}</span>{row.village_name && <small className="muted">{row.village_name}</small>}</span> },
        { key: 'status', label: 'Now', render: (row) => <span className="cell-stack"><StatusPill value={row.status} /><small className="muted">Next: {nextSteps(row.status)}</small></span> },
        { key: 'capacity', label: 'Capacity', align: 'right', render: (row) => (row.capacity ? `${formatNumber(row.capacity, 1)} ${row.capacity_uom ?? ''}` : '--') },
        { key: 'last_calibration_on', label: 'Calibrated', render: (row) => (row.last_calibration_on ? formatDate(row.last_calibration_on) : '--') },
        { key: 'next_maintenance_on', label: 'Maintenance due', render: (row) => (row.next_maintenance_on ? formatDate(row.next_maintenance_on) : '--') },
      ]}
      stats={[
        { key: 'running', label: 'Running', tone: 'ok', match: (row) => row.status === 'running' },
        { key: 'loaded', label: 'Loaded, not started', tone: 'info', match: (row) => row.status === 'new_batch_loaded' },
        { key: 'free', label: 'Free to load', tone: 'neutral', match: (row) => ['idle', 'emptied', 'repaired'].includes(row.status) },
        { key: 'repair', label: 'Under repair', tone: 'error', match: (row) => row.status === 'under_repair' },
        { key: 'due', label: 'Maintenance overdue', tone: 'warn', match: (row) => Boolean(row.next_maintenance_on && row.next_maintenance_on < today) },
      ]}
      defaults={{ chamber_type: 'kiln', status: 'idle' }}
      fields={[
        { key: 'site_id', label: 'Site', type: 'select', options: pick.processingSites, required: true,
          placeholder: 'Choose the seasoning unit or plant', hint: 'A seasoning unit, treatment plant or sawmill from Work sites. Add the site there first.' },
        { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. CHAMBER-1', hint: 'Unique within the site.' },
        { key: 'chamber_type', label: 'Type', type: 'select', options: CHAMBER_TYPES, required: true },
        { key: 'capacity', label: 'Capacity', type: 'number' },
        { key: 'capacity_uom_id', label: 'Capacity unit', type: 'select', options: pick.units, placeholder: 'e.g. cubic feet',
          required: (form) => Boolean(form.capacity) },
        { key: 'status', label: 'Now', type: 'select', options: CHAMBER_STATUS, required: true,
          hint: 'Idle → loaded → running → emptied. Running can be logged again each day; a repair can resume a cycle.' },
        { key: 'commissioned_on', label: 'Commissioned on', type: 'date', section: 'Maintenance' },
        { key: 'last_calibration_on', label: 'Last calibrated', type: 'date' },
        { key: 'next_maintenance_on', label: 'Next maintenance', type: 'date', hint: 'Listed under Renewals due as the date nears, beside licences and certificates.' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => ({
        last_calibration_on: form.last_calibration_on && String(form.last_calibration_on) > today ? 'Cannot be in the future.' : undefined,
      })}
    />
  );
}
